import { useCallback, useRef, useState } from 'react';
import type { AgentChatMessage, AgentEvent, AgentUIContext } from '../types/agent';
import { AGENT_BASE_URL } from '../lib/agentClient';
import { getAuthToken } from '../lib/apiClient';

// POSTs a chat turn to the agent service and parses the typed SSE stream,
// invoking onEvent per frame. The user's JWT is forwarded so every tool call
// runs on their behalf (never with agent-owned credentials).
export function useAgentStream(agentName: string, onEvent: (event: AgentEvent) => void) {
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const send = useCallback(async (messages: AgentChatMessage[], context: AgentUIContext) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStreaming(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`${AGENT_BASE_URL}/v1/agents/${agentName}/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ messages, context }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        let detail = '';
        try {
          const body = (await res.json()) as { error?: string };
          detail = body?.error ? ` — ${body.error}` : '';
        } catch { /* no body */ }
        const message = res.status === 429
          ? 'The agent is rate limited — wait a moment and try again.'
          : `Agent request failed (${res.status})${detail}.`;
        onEventRef.current({ type: 'error', message });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          let event = '';
          const dataLines: string[] = [];
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
          }
          const data = dataLines.join('\n');
          if (!event || !data) continue;
          try {
            onEventRef.current({ type: event, ...JSON.parse(data) } as AgentEvent);
          } catch {
            // skip malformed frame
          }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        onEventRef.current({ type: 'error', message: 'Connection to the agent was lost.' });
      }
    } finally {
      if (abortRef.current === controller) setStreaming(false);
    }
  }, [agentName]);

  const abort = useCallback(() => abortRef.current?.abort(), []);

  return { send, abort, streaming };
}
